import { StorageHelper } from './StorageHelper';
import { Global } from '../classes/Global';

// Interface for wishlist item with custom prices (v2 API format)
export interface WishlistItemV2 {
  idInStore: string;
  customPrice?: string;
  customPriceHd?: string;
}

export class ListHelper {

  // Cache for Sets to avoid recreating them on every check
  private static blocklistSetCache: { [key: string]: Set<string> } = {};
  private static wishlistSetCache: { [key: string]: Set<string> } = {};
  private static ownlistSetCache: { [key: string]: Set<string> } = {};

  // Cache for the raw wishlist data length to detect changes
  private static wishlistLengthCache: { [key: string]: number } = {};

  // Cache for parsed storage data to avoid repeated JSON.parse
  private static shortWishlistCache: any = null;
  private static shortWishlistCacheTime: number = 0;
  private static shortBlocklistCache: any = null;
  private static shortBlocklistCacheTime: number = 0;
  private static shortOwnlistCache: any = null;
  private static shortOwnlistCacheTime: number = 0;
  private static readonly CACHE_TTL_MS = 1000; // Cache valid for 1 second

  // Get cached shortWishlist (avoids repeated JSON.parse)
  private static getCachedShortWishlist(): any {
    const now = Date.now();
    if (this.shortWishlistCache && (now - this.shortWishlistCacheTime) < this.CACHE_TTL_MS) {
      return this.shortWishlistCache;
    }
    this.shortWishlistCache = StorageHelper.getInstance().get(StorageHelper.keys.shortWishlist);
    this.shortWishlistCacheTime = now;
    return this.shortWishlistCache;
  }

  // Get cached shortBlocklist (avoids repeated JSON.parse)
  private static getCachedShortBlocklist(): any {
    const now = Date.now();
    if (this.shortBlocklistCache && (now - this.shortBlocklistCacheTime) < this.CACHE_TTL_MS) {
      return this.shortBlocklistCache;
    }
    this.shortBlocklistCache = StorageHelper.getInstance().get(StorageHelper.keys.shortBlocklist);
    this.shortBlocklistCacheTime = now;
    return this.shortBlocklistCache;
  }

  // Get cached shortOwnlist (avoids repeated JSON.parse)
  private static getCachedShortOwnlist(): any {
    const now = Date.now();
    if (this.shortOwnlistCache && (now - this.shortOwnlistCacheTime) < this.CACHE_TTL_MS) {
      return this.shortOwnlistCache;
    }
    this.shortOwnlistCache = StorageHelper.getInstance().get(StorageHelper.keys.shortOwnlist);
    this.shortOwnlistCacheTime = now;
    return this.shortOwnlistCache;
  }

  // Helper method to convert array to Set with caching
  private static getSetFromArray(items: string[], cacheKey: string, cache: { [key: string]: Set<string> }): Set<string> {
    if (!items || items.length === 0) {
      return new Set();
    }
    // Use cache if available
    if (cache[cacheKey]) {
      return cache[cacheKey];
    }
    // Create Set and cache it
    const set = new Set(items);
    cache[cacheKey] = set;
    return set;
  }

  // Helper to get or create Set from wishlist items with proper caching
  private static getWishlistSet(items: any[], cacheKey: string): Set<string> {
    if (!items || items.length === 0) {
      return new Set();
    }

    // Check if cache is valid (same length = likely same data)
    const cachedLength = this.wishlistLengthCache[cacheKey];
    if (this.wishlistSetCache[cacheKey] && cachedLength === items.length) {
      return this.wishlistSetCache[cacheKey];
    }

    // Extract IDs and create Set
    let ids: string[];
    if (typeof items[0] === 'object' && items[0].idInStore) {
      // v2 format (array of objects with idInStore)
      ids = [];
      for (let i = 0; i < items.length; i++) {
        ids.push(items[i].idInStore);
      }
    } else {
      // v1 format (array of strings)
      ids = items;
    }

    const set = new Set(ids);
    this.wishlistSetCache[cacheKey] = set;
    this.wishlistLengthCache[cacheKey] = items.length;
    return set;
  }

  static isItemInWishlist(item, store?: string): boolean {
    if (!item) {
      return false;
    }

    const shortWishlist = this.getCachedShortWishlist();
    if (!shortWishlist) {
      return false;
    }

    const id = item.idInStore || item.itemID;
    if (!id) {
      return false;
    }

    const mediaType = Global.getMediaTypeFromItemType(item.itemType);
    if (!mediaType) {
      return false;
    }

    store = store || Global.getStore();
    const shortWishlistForStore = shortWishlist[store];
    if (!shortWishlistForStore) {
      return false;
    }

    const items = shortWishlistForStore[mediaType];
    if (!items || items.length === 0) {
      return false;
    }

    // Use cached Set for O(1) lookup
    const cacheKey = store + '_' + mediaType;
    const itemsSet = this.getWishlistSet(items, cacheKey);
    return itemsSet.has(id);
  }

  // Get custom prices for an item from the wishlist (v2 API format)
  // API returns: { idInStore, initialPriceValue (SD), initialHdPriceValue (HD), initial4kPriceValue (4K, Vudu only), customPrice (boolean), isRental }
  static getWishlistCustomPrices(item, store?: string): { initialPriceValue: number | null, initialHdPriceValue: number | null, initial4kPriceValue: number | null, hasCustomPrice: boolean, isRental: boolean } {
    const result = { initialPriceValue: null, initialHdPriceValue: null, initial4kPriceValue: null, hasCustomPrice: false, isRental: false };

    if (!item) {
      return result;
    }

    const shortWishlist = this.getCachedShortWishlist();
    if (!shortWishlist) {
      return result;
    }

    const id = item.idInStore || item.itemID;
    const mediaType = Global.getMediaTypeFromItemType(item.itemType);
    if (!mediaType) {
      return result;
    }

    store = store ? store : Global.getStore();
    const shortWishlistForStore = shortWishlist[store];
    if (shortWishlistForStore) {
      const items = shortWishlist[store][mediaType];
      if (!items || items.length === 0) {
        return result;
      }
      // Check if v2 format
      if (typeof items[0] === 'object' && items[0].idInStore) {
        const wishlistItem = items.find(i => i.idInStore === id);
        if (wishlistItem) {
          // initialPriceValue is the SD price (-1 means ignore)
          if (wishlistItem.initialPriceValue !== undefined && wishlistItem.initialPriceValue !== null) {
            result.initialPriceValue = Number(wishlistItem.initialPriceValue);
          }
          // initialHdPriceValue is the HD price
          if (wishlistItem.initialHdPriceValue !== undefined && wishlistItem.initialHdPriceValue !== null) {
            result.initialHdPriceValue = Number(wishlistItem.initialHdPriceValue);
          }
          // initial4kPriceValue is the 4K price (Vudu only, -1 means ignore)
          if (wishlistItem.initial4kPriceValue !== undefined && wishlistItem.initial4kPriceValue !== null) {
            result.initial4kPriceValue = Number(wishlistItem.initial4kPriceValue);
          }
          // customPrice is now a boolean indicating if user modified the price
          result.hasCustomPrice = wishlistItem.customPrice === true || wishlistItem.customPrice === 'true';
          result.isRental = wishlistItem.isRental === true || wishlistItem.isRental === 'true' || wishlistItem.isRental === 1;
        }
      }
    }
    return result;
  }

  static isItemInBlocklist(item, store?: string): boolean {
    if (!item) {
      return false;
    }

    const shortBlocklist = this.getCachedShortBlocklist();
    if (!shortBlocklist) {
      return false;
    }

    const id = item.idInStore || item.itemID;

    const mediaType = Global.getMediaTypeFromItemType(item.itemType);
    if (!mediaType) {
      return false;
    }

    store = store ? store : Global.getStore();
    const shortBlocklistForStore = shortBlocklist[store];
    if (shortBlocklistForStore) {
      const items = shortBlocklist[store][mediaType];
      if (!items || items.length === 0) {
        return false;
      }
      // Use Set for O(1) lookup instead of O(n) indexOf
      const cacheKey = `${store}_${mediaType}`;
      const itemsSet = this.getSetFromArray(items, cacheKey, this.blocklistSetCache);
      return itemsSet.has(id);
    }
    return false;
  }

  static isItemInOwnlist(item, store?: string): boolean {
    if (!item) {
      return false;
    }

    const shortOwnlist = this.getCachedShortOwnlist();
    if (!shortOwnlist) {
      return false;
    }

    const id = item.idInStore || item.itemID;

    const mediaType = Global.getMediaTypeFromItemType(item.itemType);
    if (!mediaType) {
      return false;
    }

    store = store ? store : Global.getStore();
    const shortOwnlistForStore = shortOwnlist[store];
    if (shortOwnlistForStore) {
      const items = shortOwnlist[store][mediaType];
      if (!items || items.length === 0) {
        return false;
      }
      // Use Set for O(1) lookup instead of O(n) indexOf
      const cacheKey = `${store}_${mediaType}`;
      const itemsSet = this.getSetFromArray(items, cacheKey, this.ownlistSetCache);
      return itemsSet.has(id);
    }
    return false;

  }

  // Clear caches when lists are updated
  static clearBlocklistCache(store?: string, mediaType?: string) {
    // Always clear the parsed data cache
    this.shortBlocklistCache = null;
    this.shortBlocklistCacheTime = 0;

    if (store && mediaType) {
      const cacheKey = store + '_' + mediaType;
      delete this.blocklistSetCache[cacheKey];
    } else {
      this.blocklistSetCache = {};
    }
  }

  static clearWishlistCache(store?: string, mediaType?: string) {
    // Always clear the parsed data cache
    this.shortWishlistCache = null;
    this.shortWishlistCacheTime = 0;

    if (store && mediaType) {
      const cacheKey = store + '_' + mediaType;
      delete this.wishlistSetCache[cacheKey];
      delete this.wishlistLengthCache[cacheKey];
    } else {
      this.wishlistSetCache = {};
      this.wishlistLengthCache = {};
    }
  }

  static clearOwnlistCache(store?: string, mediaType?: string) {
    // Always clear the parsed data cache
    this.shortOwnlistCache = null;
    this.shortOwnlistCacheTime = 0;

    if (store && mediaType) {
      const cacheKey = store + '_' + mediaType;
      delete this.ownlistSetCache[cacheKey];
    } else {
      this.ownlistSetCache = {};
    }
  }

  // Helper method to filter items using Set (for bulk filtering)
  static filterItemsByBlocklist(items: any[], store?: string, mediaType?: string): any[] {
    if (!items || items.length === 0) {
      return items;
    }

    const shortBlocklist = this.getCachedShortBlocklist();
    if (!shortBlocklist) {
      return items;
    }

    store = store ? store : Global.getStore();
    const shortBlocklistForStore = shortBlocklist[store];
    if (!shortBlocklistForStore) {
      return items;
    }

    // If mediaType not provided, try to determine from first item
    if (!mediaType && items.length > 0) {
      mediaType = Global.getMediaTypeFromItemType(items[0].itemType);
    }

    if (!mediaType) {
      return items;
    }

    const blocklistItems = shortBlocklistForStore[mediaType];
    if (!blocklistItems || blocklistItems.length === 0) {
      return items;
    }

    // Use Set for O(1) lookup
    const cacheKey = `${store}_${mediaType}`;
    const blocklistSet = this.getSetFromArray(blocklistItems, cacheKey, this.blocklistSetCache);

    return items.filter(item => {
      const id = item.idInStore || item.itemID;
      return !blocklistSet.has(id);
    });
  }

}
